export type GeometryMode = 'point' | 'draw' | 'trace' | 'coordinates' | 'link' | 'enrich';

/** Representative lat/lng for area/line GeoJSON (ring average or line midpoint). */
export function getGeometryAnchor(geometry: GeoJSON.Geometry | null | undefined): { lat: number; lng: number } | null {
  if (!geometry) return null;
  if (geometry.type === 'Point' && geometry.coordinates?.length >= 2) {
    const [lng, lat] = geometry.coordinates as [number, number];
    return { lat, lng };
  }
  if (geometry.type === 'Polygon' && geometry.coordinates?.[0]?.length) {
    const ring = geometry.coordinates[0] as number[][];
    let slat = 0;
    let slng = 0;
    for (const p of ring) {
      slng += p[0];
      slat += p[1];
    }
    const n = ring.length;
    return { lat: slat / n, lng: slng / n };
  }
  if (geometry.type === 'MultiPolygon' && geometry.coordinates?.[0]?.[0]?.length) {
    return getGeometryAnchor({ type: 'Polygon', coordinates: geometry.coordinates[0] });
  }
  if (geometry.type === 'LineString' && geometry.coordinates?.length) {
    const coords = geometry.coordinates as number[][];
    const idx = Math.floor(coords.length / 2);
    return { lat: coords[idx][1], lng: coords[idx][0] };
  }
  return null;
}

export function getContributionAnchor(
  geometryMode: GeometryMode | null,
  selectedLocation: { lat: number; lng: number } | null,
  drawnGeometry: GeoJSON.Geometry | null,
  tracePoints: Array<{ lat: number; lng: number }>
): { lat: number; lng: number } | null {
  if (!geometryMode) return null;
  if ((geometryMode === 'point' || geometryMode === 'coordinates' || geometryMode === 'link' || geometryMode === 'enrich') && selectedLocation) return selectedLocation;
  if (geometryMode === 'trace') {
    if (tracePoints.length === 0) return null;
    const i = Math.floor(tracePoints.length / 2);
    return tracePoints[i];
  }
  if (geometryMode === 'draw' && drawnGeometry) {
    return getGeometryAnchor(drawnGeometry);
  }
  return null;
}

/** Split comma-separated address fragments and dedupe (case-insensitive). */
export function flattenDedupeAddressSegments(...parts: (string | undefined | null)[]): string[] {
  const out: string[] = [];
  for (const raw of parts) {
    if (!raw?.trim()) continue;
    for (const piece of raw.split(',')) {
      const s = piece.trim();
      if (!s) continue;
      const norm = s.toLowerCase();
      if (out.some((existing) => existing.toLowerCase() === norm)) continue;
      out.push(s);
    }
  }
  return out;
}

/** Join address segments with Ghana at most once at the end. */
export function joinAddressSegments(segments: string[], { ensureGhana = false } = {}): string {
  const nonGhana = segments.filter((s) => s.toLowerCase() !== 'ghana');
  const hadGhana = segments.some((s) => s.toLowerCase() === 'ghana');
  if (nonGhana.length === 0) return hadGhana || ensureGhana ? 'Ghana' : '';
  if (ensureGhana || hadGhana) return [...nonGhana, 'Ghana'].join(', ');
  return nonGhana.join(', ');
}

/** Locality segments for address previews — omit district/region when 2+ locality levels exist. */
export function composeLocalityPreviewSegments(
  suburb: string,
  city: string,
  district: string,
): string[] {
  const normalized = normalizeLocalityFields(suburb, city);
  const locality: string[] = [];
  if (normalized.suburb) locality.push(normalized.suburb);
  if (
    normalized.city
    && !locality.some((segment) => segment.toLowerCase() === normalized.city.toLowerCase())
  ) {
    locality.push(normalized.city);
  }
  if (!locality.length && district.trim()) {
    locality.push(district.trim());
  }
  return locality;
}

export function composeAddressLine(parts: {
  name?: string;
  street: string;
  suburb: string;
  city: string;
  district: string;
  region: string;
  ghanaPostGps?: string;
}): string {
  const segments = flattenDedupeAddressSegments(
    parts.name,
    parts.street,
    ...composeLocalityPreviewSegments(parts.suburb, parts.city, parts.district),
    parts.ghanaPostGps,
  );
  return joinAddressSegments(segments, { ensureGhana: Boolean(parts.district || parts.region || parts.city || parts.suburb) });
}

/** Road contributions: road name is the street line (no geocoded street in preview). */
export function composeRoadAddressLine(parts: {
  roadName: string;
  suburb: string;
  city: string;
  district: string;
  region: string;
  ghanaPostGps?: string;
}): string {
  return composeAddressLine({
    street: parts.roadName.trim(),
    suburb: parts.suburb,
    city: parts.city,
    district: parts.district,
    region: parts.region,
    ghanaPostGps: parts.ghanaPostGps,
  });
}

/** Map label for road enrich — canonical name or generated_name/ref from feature lookup. */
export function resolveRoadEnrichDisplayName(feature: {
  name?: string | null;
  details?: Record<string, unknown> | null;
}): string {
  const direct = feature.name?.trim();
  if (direct) return direct;
  const d = feature.details ?? {};
  for (const key of ['name', 'name_en', 'name_local'] as const) {
    const val = d[key];
    if (typeof val === 'string' && val.trim()) return val.trim();
  }
  const generated = d.generated_name;
  if (typeof generated === 'string' && generated.trim()) return generated.trim();
  const ref = d.ref ?? d.generated_ref;
  if (typeof ref === 'string' && ref.trim()) return ref.trim();
  return '';
}

/** Submit label: typed road name, then existing enrich label, then category/type fallback. */
export function resolveRoadContributionName(parts: {
  roadName: string;
  enrichDisplayName?: string | null;
  categoryLabel?: string | null;
  featureTypeLabel?: string | null;
}): string {
  const typed = parts.roadName.trim();
  if (typed) return typed;
  const existing = parts.enrichDisplayName?.trim();
  if (existing) return existing;
  return parts.categoryLabel?.trim() || parts.featureTypeLabel?.trim() || 'Contribution';
}

/**
 * Building / POI address line aligned with backend format_address (ADDRESS_STANDARD.md):
 * name, street, suburb/city locality — district only as fallback, no region.
 */
export function composeNumberedStreet(
  houseNumber?: string | null,
  streetLine?: string | null,
): string | undefined {
  const hn = houseNumber?.trim() ?? '';
  const street = streetLine?.trim() ?? '';
  if (!hn && !street) return undefined;
  if (!hn) return street || undefined;
  if (!street) return hn;
  const lower = street.toLowerCase();
  const prefix = hn.toLowerCase();
  if (lower === prefix || lower.startsWith(`${prefix} `)) return street;
  return `${hn} ${street}`;
}

/** Default building label when no name is provided but a house number exists. */
export function resolveBuildingDisplayName(
  name: string | undefined,
  houseNumber?: string,
): string | undefined {
  const n = name?.trim() ?? '';
  if (n) return n;
  const hn = houseNumber?.trim() ?? '';
  if (hn) return `Building ${hn}`;
  return undefined;
}

/** True when a stored name is really a street address line, not a building label. */
export function isAddressLikeBuildingName(name: string | undefined | null): boolean {
  const n = name?.trim() ?? '';
  if (!n) return false;
  if (/^\d+\s+.+?(road|street|avenue|close|lane|drive|way|boulevard|highway)\b/i.test(n)) {
    return true;
  }
  if (/^\d+\s+residential road\b/i.test(n)) {
    return true;
  }
  return false;
}

/** Name field prefill for suggest-edit on buildings — never the full street line. */
export function buildingNamePrefillForSuggestEdit(place: {
  name?: string | null;
  houseNumber?: string | null;
}): string {
  const name = place.name?.trim() ?? '';
  const houseNumber = place.houseNumber?.trim() ?? '';
  if (name && !isAddressLikeBuildingName(name)) return name;
  if (houseNumber) return houseNumber;
  return '';
}

/** Street line for editing — house number is shown separately and must not be retyped. */
export function streetLineWithoutHouseNumber(
  street: string | undefined | null,
  houseNumber?: string | null,
): string {
  const line = street?.trim() ?? '';
  const hn = houseNumber?.trim() ?? '';
  if (!line || !hn) return line;
  const lower = line.toLowerCase();
  const prefix = hn.toLowerCase();
  if (lower === prefix) return '';
  if (lower.startsWith(`${prefix} `)) return line.slice(hn.length).trim();
  return line;
}

/** Building, POI, and landmark contributions share numbered-street address editing. */
export function usesBuildingHostAddressFormat(featureTypeId?: string | null): boolean {
  return featureTypeId === 'building' || featureTypeId === 'poi' || featureTypeId === 'landmark';
}

export interface BuildingHostAddressPrefill {
  name: string;
  houseNumber: string;
  street: string;
}

/** Normalize building-host name + street fields for contribution prefills. */
export function resolveBuildingHostAddressPrefill(input: {
  name?: string | null;
  houseNumber?: string | null;
  street?: string | null;
  addrStreet?: string | null;
  frontageStreetAddress?: string | null;
}): BuildingHostAddressPrefill {
  const houseNumber = input.houseNumber?.trim() ?? '';
  const rawStreet =
    input.street?.trim()
    || input.addrStreet?.trim()
    || input.frontageStreetAddress?.trim()
    || '';
  return {
    name: buildingNamePrefillForSuggestEdit({ name: input.name, houseNumber }),
    houseNumber,
    street: streetLineWithoutHouseNumber(rawStreet, houseNumber),
  };
}

/** House number from reverse-geocode address_components when present. */
export function extractReverseGeocodeBuildingNumber(
  result: { address_components?: { building_number?: string | null } | null } | null | undefined,
): string | undefined {
  return result?.address_components?.building_number?.trim() || undefined;
}

export function resolveBuildingStreetSegment(params: {
  street?: string;
  houseNumber?: string;
  frontageStreetAddress?: string;
}): string | undefined {
  const hn = params.houseNumber?.trim() ?? '';
  let streetLine = params.street?.trim() ?? '';
  const frontage = params.frontageStreetAddress?.trim() ?? '';
  if (!streetLine && frontage) streetLine = frontage;
  if (!streetLine) return hn || undefined;
  if (hn) return composeNumberedStreet(hn, streetLine) ?? streetLine;
  return streetLine;
}

export function composeBuildingAddressLine(parts: {
  name?: string;
  street: string;
  suburb: string;
  city: string;
  district: string;
  ghanaPostGps?: string;
  houseNumber?: string;
  frontageStreetAddress?: string;
}): string {
  const suburb = parts.suburb?.trim() ?? '';
  const city = parts.city?.trim() ?? '';
  const district = parts.district?.trim() ?? '';

  const locality: string[] = [];
  if (suburb) locality.push(suburb);
  if (city && !locality.some((s) => s.toLowerCase() === city.toLowerCase())) {
    locality.push(city);
  }
  if (!locality.length && district) {
    locality.push(district);
  }

  const nameSegment = resolveBuildingDisplayName(parts.name, parts.houseNumber);
  const streetSegment = resolveBuildingStreetSegment({
    street: parts.street,
    houseNumber: parts.houseNumber,
    frontageStreetAddress: parts.frontageStreetAddress,
  });

  const segments = flattenDedupeAddressSegments(
    nameSegment,
    streetSegment,
    ...locality,
    parts.ghanaPostGps,
  );
  return joinAddressSegments(segments, { ensureGhana: false });
}

export function composePlaceContextLine(parts: {
  placeName?: string;
  suburb: string;
  city: string;
  district: string;
  region: string;
}): string {
  const placeNorm = parts.placeName?.trim().toLowerCase() ?? '';
  const segments = composeLocalityPreviewSegments(parts.suburb, parts.city, parts.district)
    .filter((s) => !placeNorm || s.toLowerCase() !== placeNorm);
  return segments.join(', ');
}

export interface ContributionAddressFields {
  street: string;
  suburb: string;
  city: string;
  district: string;
  region: string;
}

/** True when the POI/building already has contributor or OSM structured address data. */
export function placeHasStoredAddress(place: {
  addrStreet?: string | null;
  addrSuburb?: string | null;
  addrCity?: string | null;
  district?: string | null;
  region?: string | null;
}): boolean {
  return Boolean(
    place.addrStreet?.trim()
    || place.addrSuburb?.trim()
    || place.addrCity?.trim()
    || place.district?.trim()
    || place.region?.trim(),
  );
}

/** Best structured address fields from a map place — never uses the full formatted address as street. */
export function placeAddressPrefill(place: {
  placeType?: string | null;
  name?: string | null;
  addrStreet?: string | null;
  addrSuburb?: string | null;
  addrCity?: string | null;
  streetAddress?: string | null;
  district?: string | null;
  region?: string | null;
}): Partial<ContributionAddressFields> {
  const street = place.addrStreet?.trim()
    || (place.placeType === 'street' && place.name?.trim() ? place.name.trim() : undefined)
    || (place.streetAddress?.trim() && !place.streetAddress.includes(',') ? place.streetAddress.trim() : undefined);

  const suburbRaw = place.addrSuburb?.trim() || '';
  const cityRaw = place.addrCity?.trim() || '';
  const locality = normalizeLocalityFields(suburbRaw, cityRaw);

  return {
    street,
    suburb: locality.suburb || undefined,
    city: locality.city || undefined,
    district: place.district?.trim() || undefined,
    region: place.region?.trim() || undefined,
  };
}

/** Fill address fields from reverse geocode without wiping values we already have. */
export function mergeAddressFields(
  current: Partial<ContributionAddressFields>,
  incoming: Partial<ContributionAddressFields>,
): ContributionAddressFields {
  return {
    street: current.street?.trim() || incoming.street?.trim() || '',
    suburb: current.suburb?.trim() || incoming.suburb?.trim() || '',
    city: current.city?.trim() || incoming.city?.trim() || '',
    district: current.district?.trim() || incoming.district?.trim() || '',
    region: current.region?.trim() || incoming.region?.trim() || '',
  };
}

export function normalizeLocalityFields(suburb: string, city: string): { suburb: string; city: string } {
  let nextSuburb = suburb.trim();
  let nextCity = city.trim();

  if (nextCity.includes(',')) {
    const parts = flattenDedupeAddressSegments(nextCity);
    if (nextSuburb) {
      while (parts.length && parts[0].toLowerCase() === nextSuburb.toLowerCase()) {
        parts.shift();
      }
      nextCity = parts.join(', ');
    } else if (parts.length >= 2) {
      nextSuburb = parts[0];
      nextCity = parts.slice(1).join(', ');
    }
  }

  if (nextSuburb && nextCity && nextSuburb.toLowerCase() === nextCity.toLowerCase()) {
    nextCity = '';
  }

  return { suburb: nextSuburb, city: nextCity };
}

export function applyAddressFields(
  fields: ContributionAddressFields,
  setters: {
    setStreet: (v: string) => void;
    setSuburb: (v: string) => void;
    setCity: (v: string) => void;
    setDistrict: (v: string) => void;
    setRegion: (v: string) => void;
  },
): void {
  const locality = normalizeLocalityFields(fields.suburb, fields.city);
  setters.setStreet(fields.street);
  setters.setSuburb(locality.suburb);
  setters.setCity(locality.city);
  setters.setDistrict(fields.district);
  setters.setRegion(fields.region);
}

export function emptyContributionAddressFields(): ContributionAddressFields {
  return { street: '', suburb: '', city: '', district: '', region: '' };
}

/** Null-safe extraction from /v1/reverse-geocode (data may be null). */
export function extractReverseGeocodeComponents(
  result: { address_components?: Partial<ContributionAddressFields> | null } | null | undefined,
): Partial<ContributionAddressFields> {
  const c = result?.address_components ?? {};
  return {
    street: c.street?.trim() || undefined,
    suburb: c.suburb?.trim() || undefined,
    city: c.city?.trim() || undefined,
    district: c.district?.trim() || undefined,
    region: c.region?.trim() || undefined,
  };
}

export function hasGeocodedAddressComponents(fields: Partial<ContributionAddressFields>): boolean {
  return Boolean(
    fields.region?.trim()
    || fields.district?.trim()
    || fields.city?.trim()
    || fields.suburb?.trim()
    || fields.street?.trim(),
  );
}

/** Full reverse geocode (building + admin) populates suburb/street better than admin-only. */
export function contributionReverseGeocodeAdminOnly(_featureTypeId?: string | null): boolean {
  return false;
}

/** Area / suburb field for transport stops, roads, trees, etc. — not only POI/building. */
export function contributionShowsSuburbField(
  featureTypeId: string | undefined | null,
  isFullDetails: boolean,
): boolean {
  if (featureTypeId === 'place' || featureTypeId === 'official_area') return false;
  if (isFullDetails) return true;
  return Boolean(featureTypeId);
}

/** Normalize geocoded address fields for form state (no extra API calls). */
export function finalizeGeocodedAddressFields(fields: ContributionAddressFields): ContributionAddressFields {
  const locality = normalizeLocalityFields(fields.suburb, fields.city);
  return {
    street: fields.street.trim(),
    suburb: locality.suburb,
    city: locality.city,
    district: fields.district.trim(),
    region: fields.region.trim(),
  };
}

/** Match geocoded admin name to a canonical boundary row for strict dropdown fields. */
export function matchBoundaryName(
  query: string,
  options: ReadonlyArray<{ name: string }>,
): string | null {
  const q = query.trim();
  if (!q || !options.length) return null;
  const lower = q.toLowerCase();
  const exact = options.find((o) => o.name.toLowerCase() === lower);
  if (exact) return exact.name;
  const starts = options.find((o) => o.name.toLowerCase().startsWith(lower));
  if (starts) return starts.name;
  const contains = options.find((o) => o.name.toLowerCase().includes(lower));
  return contains?.name ?? null;
}
